import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { logDev } from "@/lib/logger";
import LeadForm from "@/components/LeadForm";
import { PhoneCall, CheckCircle2, AlertCircle } from "lucide-react";

const MISSING_LABELS = { name: "full name", mobile: "mobile number" };

/** State machine for the one-click callback request. */
function useCallbackRequest(propertyId, user, refresh) {
  const initiallyRequested = !!user && (user.callback_property_ids || []).includes(propertyId);
  const [already, setAlready] = useState(initiallyRequested);
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(null); // ["name","mobile"] or null

  const submit = async () => {
    if (busy || already) return;
    setBusy(true);
    setMissing(null);
    try {
      await api.post(`/properties/${propertyId}/request-callback`);
      setAlready(true);
      // Refresh auth user so /auth/me reflects the new callback_property_ids
      // for future property page loads (still zero flash on repeat visits).
      refresh?.().catch((err) => logDev.warn("auth refresh after callback failed:", err));
      toast.success("Callback requested. Tharlo will reach out shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "profile_incomplete") {
        setMissing(detail.missing || []);
      } else if (err?.response?.status === 429) {
        toast.error("Too many callback requests. Please try again later.");
      } else {
        toast.error("Could not submit callback request. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return { already, busy, missing, submit };
}

function ProfileIncompleteNotice({ missing }) {
  return (
    <div data-testid="callback-profile-incomplete" className="border border-yellow-300 bg-yellow-50 p-4 rounded-none">
      <div className="flex items-start gap-2 mb-2">
        <AlertCircle className="w-4 h-4 text-yellow-700 mt-0.5 shrink-0" />
        <div className="text-sm text-yellow-900">
          Please add your <strong>{missing.map((m) => MISSING_LABELS[m] || m).join(" and ")}</strong> in your profile to request a callback.
        </div>
      </div>
      <Link
        to="/dashboard?s=profile"
        data-testid="callback-goto-profile"
        className="btn-primary w-full !py-2.5 text-center inline-block"
      >
        Add details in Profile
      </Link>
    </div>
  );
}

function CallbackConfirmed({ contactNumber }) {
  return (
    <div data-testid="callback-already-requested" className="border border-green-300 bg-green-50 p-4">
      <div className="flex items-center gap-2 text-green-800 text-sm font-semibold">
        <CheckCircle2 className="w-4 h-4" />
        Callback requested
      </div>
      <p className="text-xs text-green-800/80 mt-1">
        A Tharlo advisor will contact you on <strong>{contactNumber}</strong> shortly.
      </p>
    </div>
  );
}

/**
 * Signed-in one-click "Request Callback" for a property.
 * Guests fall through to the existing LeadForm.
 */
export default function CallbackButton({ propertyId, propertyTitle = "" }) {
  const { user, refresh } = useAuth();
  const { already, busy, missing, submit } = useCallbackRequest(propertyId, user, refresh);

  if (!user) {
    // Guest fallback — keep the existing full lead form (captcha required)
    return <LeadForm propertyId={propertyId} source="property_detail" compact />;
  }

  const contactNumber = user.mobile || user.phone || "your registered number";

  if (missing && missing.length) return <ProfileIncompleteNotice missing={missing} />;
  if (already) return <CallbackConfirmed contactNumber={contactNumber} />;

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        You&apos;re signed in as <strong className="text-ink">{user.full_name || user.name}</strong>. We&apos;ll call you on <strong className="text-ink">{contactNumber}</strong>.
      </p>
      <button
        type="button"
        onClick={submit}
        disabled={busy}
        data-testid="callback-request-btn"
        className="btn-gold w-full flex items-center justify-center gap-2"
      >
        <PhoneCall className="w-4 h-4" /> {busy ? "Requesting..." : "Request Callback"}
      </button>
      {propertyTitle && (
        <p className="text-[11px] text-muted-foreground">
          For <em className="not-italic text-foreground/80">{propertyTitle}</em>. Your details go directly to Tharlo advisors.
        </p>
      )}
    </div>
  );
}
